import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import selectOnFocus from '../../../../../../modules/autoselect';

type TitleSearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  displayText: string;
  icon: React.ReactNode;
  onBlur?: () => void;
  disabled?: boolean;
};

const TitleSearchInput = ({
  value,
  onChange,
  placeholder = 'חפש...',
  displayText,
  icon,
  onBlur,
  disabled = false,
}: TitleSearchInputProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = useCallback(() => {
    if (disabled) return;
    setIsEditing(true);
  }, [disabled]);

  const handleBlur = useCallback(() => {
    if (!value.trim()) {
      setIsEditing(false);
    }
    if (onBlur) {
      onBlur();
    }
  }, [value, onBlur]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        setIsEditing(false);
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    },
    []
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  if (isEditing || value.trim()) {
    return (
      <Wrapper>
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          onClick={selectOnFocus}
          aria-label={`Search ${displayText}`}
        />
        {icon && <IconWrapper>{icon}</IconWrapper>}
      </Wrapper>
    );
  }

  return (
    <Wrapper onClick={handleClick} $isClickable={!disabled}>
      <Text>{displayText}</Text>
      {icon && <IconWrapper>{icon}</IconWrapper>}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $isClickable?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5em;
  cursor: ${props => (props.$isClickable ? 'pointer' : 'default')};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    opacity: ${props => (props.$isClickable ? 0.8 : 1)};
  }
`;

const Text = styled.span`
  font-weight: 600;
  font-size: 1.1em;
  color: var(--primary-green, #297F76);
  font-family: 'Assistant', sans-serif;
  line-height: 1;
  user-select: none;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-weight: 600;
  /* font-size: 1em; */
  color: var(--primary-green, #297F76);
  font-family: 'Assistant', sans-serif;
  line-height: 1;
  text-align: right;
  direction: rtl;
  padding: 0;
  margin: 0;

  &::placeholder {
    color: var(--primary-green, #297F76);
    opacity: 0.5;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export default TitleSearchInput;

